import { useState } from "react";
import { TableTheme, ViewState } from "../types";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "./ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
} from "./ui/tooltip";
import { ChevronDown, Trash, Save } from "lucide-react";

export const ViewSelector = ({
  views,
  currentView,
  onViewSelect,
  onDeleteView,
  theme,
}: {
  views: ViewState[];
  currentView: ViewState | null;
  onViewSelect: (view: ViewState) => void;
  onDeleteView: (viewId: string) => void;
  theme: TableTheme;
}) => {
  if (views.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        asChild
        className={`rounded-full ${theme.rowEvenBg} ${theme.rowText} ${theme.buttonSecondaryBorder} ${theme.buttonSecondaryHover}`}
      >
        <Button variant="outline" size="sm">
          {currentView ? currentView.name : "Select View"}
          <ChevronDown className={`ml-2 h-4 w-4`} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className={`w-[220px] rounded-[1rem] ${theme.rowEvenBg}`}
      >
        {views.map((view) => (
          <div
            key={view.id}
            className={`flex items-center justify-between px-2 py-1 rounded-[1rem] ${theme.rowText} hover:bg-gray-100`}
          >
            <button
              className={`flex-1 text-left px-2 py-1 text-sm rounded-[1rem] hover:bg-gray-50`}
              onClick={() => onViewSelect(view)}
            >
              <span>{view.name}</span>
            </button>
            <button
              className="h-6 w-6 p-0 flex items-center justify-center rounded hover:bg-red-100"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteView(view.id);
              }}
            >
              <Trash className="h-3 w-3" />
            </button>
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const SaveViewDialog = ({
  onSaveView,
  theme,
}: {
  onSaveView: (name: string, isDefault: boolean) => void;
  theme: TableTheme;
}) => {
  const [open, setOpen] = useState(false);
  const [viewName, setViewName] = useState("");
  const [isDefault, setIsDefault] = useState(false);

  const handleSave = () => {
    if (viewName.trim()) {
      onSaveView(viewName.trim(), isDefault);
      setViewName("");
      setIsDefault(false);
      setOpen(false);
    }
  };

  const handleCancel = () => {
    setViewName("");
    setIsDefault(false);
    setOpen(false);
  };

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className={`inline-flex items-center justify-center whitespace-nowrap rounded-full text-sm font-medium h-9 w-9 cursor-pointer hover:bg-gray-200 transition-colors`}
              onClick={() => setOpen(true)}
            >
              <Save className="h-3 w-3" />
            </button>
          </TooltipTrigger>
          <TooltipContent
            className={`${theme.buttonPrimaryBg} ${theme.buttonPrimaryText}`}
          >
            <p>Save View</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Current View</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label
                htmlFor="viewName"
                className="block text-sm font-medium mb-2"
              >
                View Name
              </label>
              <Input
                id="viewName"
                placeholder="Enter view name..."
                value={viewName}
                onChange={(e) => setViewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSave();
                  }
                }}
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isDefault"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="rounded"
              />
              <label htmlFor="isDefault" className="text-sm">
                Set as default view
              </label>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={handleCancel} type="button">
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={!viewName.trim()}
                type="button"
              >
                Save View
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const DeleteViewDialog = ({
  viewToDelete,
  onConfirm,
  onCancel,
}: {
  viewToDelete: ViewState | null;
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  return (
    <Dialog open={!!viewToDelete} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete View</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-600">
          Are you sure you want to delete the view &quot;{viewToDelete?.name}
          &quot;? This action cannot be undone.
        </p>
        <div className="flex justify-end space-x-2 mt-4">
          <Button variant="outline" onClick={onCancel} type="button">
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} type="button">
            Delete
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
